// Pure helpers for the master Catalog tab and the master Index Composer. No React, no API.

// Catalog status → badge colour. One helper per page (ui-standards); unknown values fall back to gray.
const STATUS_BADGE = {
  preferred: 'stui-badge-green',
  active: 'stui-badge-blue',
  '': 'stui-badge-blue',
  'do not use': 'stui-badge-orange',
  obsolete: 'stui-badge-gray',
  unreviewed: 'stui-badge-amber',
};
export const statusBadgeClass = status =>
  STATUS_BADGE[String(status ?? '').trim().toLowerCase()] ?? 'stui-badge-gray';

// What Compare/Review says about a project sheet's datasheet against the catalog's.
export const DATASHEET_STATE_LABEL = {
  None: '',
  NotInCatalog: 'Catalog has no datasheet',
  Same: '',
  ValuesDiffer: 'Values differ',
  OtherRevision: 'Different revision',
};

// Rows whose datasheet can be opened in the difference viewer.
export const hasDatasheetDiff = row =>
  row?.dsConfig != null && ['ValuesDiffer', 'OtherRevision', 'NotInCatalog'].includes(row.datasheet);

// One line describing everything that differs on a Compare/Review row.
export function differenceSummary(row) {
  const parts = (row?.fieldDiffs ?? []).map(d => d.field);
  const ds = DATASHEET_STATE_LABEL[row?.datasheet];
  if (ds) parts.push(ds);
  return parts.join(', ');
}

// Row counts for one database's Compare/Review result.
export function compareCounts(result) {
  const s = result?.sheets ?? {};
  const m = result?.manufacturers ?? {};
  return {
    notInCatalog: s.notInCatalog?.length ?? 0,
    differs: s.differs?.length ?? 0,
    nonConforming: s.nonConforming?.length ?? 0,
    mfrNotInCatalog: m.notInCatalog?.length ?? 0,
    mfrDiffers: m.differs?.length ?? 0,
  };
}

// A catalog sheet in the shape the Index Composer reads from a project's Index_Sheet rows.
export const toComposerInstrument = sheet => ({
  id: sheet.id,
  index_Sheet: sheet.indexSheet,
  dS_Config: sheet.dsConfig ?? null,
  dS_Config_Display: sheet.dsIndex ?? null,
  description: sheet.description,
  model: sheet.model,
  specification: sheet.specification,
  type: sheet.type,
  status: sheet.status,
  manufacturer: sheet.manufacturerId,
  manufacturer_Display: sheet.manufacturer,
});

// Where a box sits on its form. Two revisions of one datasheet have different box IDs, so this is
// how a box on one revision is found on the other.
const placeKey = b => `${b.sheet ?? ''}|${b.dsZone ?? ''}|${b.lineNo ?? ''}|${b.order ?? ''}`;

const uniqueByPlace = boxes => {
  const seen = new Map();
  const dup = new Set();
  (boxes || []).forEach(b => {
    const k = placeKey(b);
    if (seen.has(k)) dup.add(k); else seen.set(k, b);
  });
  dup.forEach(k => seen.delete(k));
  return seen;
};

/**
 * Lines `right` up with `left` for the difference viewer when the two are different revisions
 * (no box ID in common). Each right box whose place (sheet, zone, line, order) is unique on both
 * sides takes the matching left box's ID, and its value moves with it, so the viewer compares
 * like with like instead of listing every box as removed and added. Boxes with no unique match
 * keep their own IDs. When the two already share IDs nothing changes.
 *
 * Returns { boxes, values } for the right side; values is an object keyed by box ID.
 */
export function alignRevision(leftBoxes, rightBoxes, rightValues) {
  const right = rightBoxes || [];
  const leftIds = new Set((leftBoxes || []).map(b => String(b.id)));
  const valuesObj = rightValues instanceof Map ? Object.fromEntries(rightValues) : { ...(rightValues || {}) };
  if (right.some(b => leftIds.has(String(b.id)))) return { boxes: right, values: valuesObj };

  const leftByPlace = uniqueByPlace(leftBoxes);
  const rightByPlace = uniqueByPlace(right);
  const newId = new Map();
  rightByPlace.forEach((b, k) => { const l = leftByPlace.get(k); if (l) newId.set(String(b.id), l.id); });

  const boxes = right.map(b => (newId.has(String(b.id)) ? { ...b, id: newId.get(String(b.id)) } : b));
  const values = {};
  Object.entries(valuesObj).forEach(([id, v]) => {
    values[newId.has(String(id)) ? newId.get(String(id)) : id] = v;
  });
  return { boxes, values };
}
